import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BaseApiService } from '../../shared/base-service/base-api.service';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { AuthorizedPresenceHistory } from '../models/authorized-presence-history.model';
import { CustomerAffiliation } from '../models/customer-affiliation.model';
import { CustomerIdentificationLookup } from '../models/customer-identification-lookup.model';
import { CustomerIdentification } from '../models/customer-identification.model';
import { CustomerIndividualBiometric } from '../models/customer-individual-biometric.model';
import { CustomerIndividualMedicalAlertType } from '../models/customer-individual-medical-alert-type.model';
import { CustomerIndividualMedicalCondition } from '../models/customer-individual-medical-condition.model';
import { CustomerIndividualMedicalLookup } from '../models/customer-individual-medical-lookup.model';
import { CustomerMvdNowStatus } from '../models/customer-mvd-now-status.model';
import { CustomerRepresentative } from '../representative/customer-representative.model';
import { CustomerName } from '../models/customer-name.model';
import { MvdNowUserSearchCriteria } from '../MVDNow/models/mvdnow-user-search-criteria.model';
import { MvdNowUserSearchResult } from '../MVDNow/models/mvdnow-user-search-result.model';
import { MobileIdentificationCredentialActionRequest } from '../MVDNow/models/mobile-identification-credential-action-request.model';
import { MergeCustomer } from '../models/merge-customer.model';
import { CustomerAddress } from '../models/customer-address.model';

@Injectable()
export class CustomerManageService extends BaseApiService {
    constructor(private _loggingService: LoggingService) {
        super();
    }

    public getIndividual = (customerId: string): Observable<any> => this.getMaxResult('Customer/' + customerId);

    public updateStatus = (customerNumberId: number, customerStatusTypeId: number): Observable<any> => this.post(`customer/Status/${customerNumberId}/${customerStatusTypeId}`, null);

    public getOrganization = (customerId: string): Observable<any> => this.getMaxResult('Customer/' + customerId);

    public getAffiliationMembers = (customerId, affiliationRoleTypeId): Observable<any> => this.getMaxResult('Customer/Affiliations/' + customerId + '/Members/' + affiliationRoleTypeId);

    public saveIndividual = (IndividualDetailViewModel): Observable<any> => this.post('Customer/Individual', IndividualDetailViewModel, this.replacer);

    public saveOrganization = (OrganizationDetailViewModel): Observable<any> => this.post('Customer/Organization', OrganizationDetailViewModel, this.replacer);

    public generateOIN = (): Observable<any> => this.getMaxResult('Customer/OIN/New/16/16');

    public logCustomerRequest = (customerNumberId: number, action: string): Observable<any> => this.getMaxResult('Customer/' + customerNumberId + '/Log/' + action);

    public getCustomerType = (customerNumberId: number): Observable<any> => this.getMaxResult('Customer/' + customerNumberId + '/Type');

    /* NAMES*/
    public getCustomerLegalName = (customerNumberId: number): Observable<CustomerName> => this.getMaxResult('Customer/' + customerNumberId + '/Names/Legal');

    public getCustomerName = (nameId: number): Observable<any> => this.getMaxResult('Customer/Names/' + nameId);

    public getNames = (customerNumberId: number): Observable<any> => this.getMaxResult('Customer/' + customerNumberId + '/Names');

    public saveName = (customerName: any): Observable<any> => this.post('Customer/Names', customerName, this.replacer)
        .pipe(catchError((error) => this.handleError(error)));

    public saveChangeCustomerType = (customerName: any, customerTypeId: number): Observable<any> => this.post('Driver/Customer/SaveChangeCustomerType/' + customerTypeId, customerName, this.replacer)
        .pipe(catchError((error) => this.handleError(error)));

    public validateMergeCustomer = (mergeCustomer: MergeCustomer): Observable<any> => this.post('Customer/ValidateMergeCustomer', mergeCustomer, this.replacer)
        .pipe(catchError((error) => this.handleError(error)));

    public mergeCustomer = (mergeCustomer: MergeCustomer): Observable<any> => this.post('Customer/MergeCustomer', mergeCustomer, this.replacer)
        .pipe(catchError((error) => this.handleError(error)));

    /* BIOMETRICS*/
    public getCustomerBiometrics = (customerNumberId: number): Observable<any> => this.getMaxResult('Customer/' + customerNumberId + '/Biometrics');

    /* ADDRESS*/
    public getAddress = (addressNumberId: number): Observable<any> => this.getMaxResult('Address/address/' + addressNumberId);

    public getCustomerAddressess = (customerNumberId: number): Observable<CustomerAddress[]> => this.getMaxResult('Address/address/Customer/' + customerNumberId);

    public getHistoricalCustomerAddressess = (customerNumberId: number): Observable<any> => this.getMaxResult('Address/address/Customer/' + customerNumberId + '/history');

    public saveAddress = (customerId: string, CustomerAddress): Observable<any> => this.post('Address/Address', CustomerAddress, this.replacer)
        .pipe(catchError((error) => this.handleError(error)));

    /* CONTACTS*/
    public getCustomerContacts = (customerNumberId: number): Observable<any> => this.getMaxResult('Customer/' + customerNumberId + '/Contacts');

    public getCustomerContact = (contactId: number): Observable<any> => this.getMaxResult('Customer/Contacts/' + contactId);

    public getHistoricalCustomerContacts = (customerNumberId: number): Observable<any> => this.getMaxResult('Customer/' + customerNumberId + '/Contacts/History');

    public saveContact = (customerContact: any): Observable<any> => this.post('Customer/Contacts', customerContact, this.replacer)
        .pipe(catchError((error) => this.handleError(error)));

    public deleteContact = (customerContactId: any): Observable<any> => this.getMaxResult('Customer/Contacts/Delete/' + customerContactId);

    public getBestAvailableEmail = (customerNumberId: number): Observable<any> => this.getMaxResult('Customer/Record/Contacts/bestavailable/email/' + customerNumberId);

    /* IDENTIFICATIONS*/
    public getCustomerIdentification = (identificationId: number): Observable<CustomerIdentification> => this.getMaxResult('Customer/Identifications/' + identificationId);

    public getCustomerIdentifications = (customerNumberId: number): Observable<CustomerIdentification[]> => this.getMaxResult('Customer/' + customerNumberId + '/Identifications');

    public getHistoricalCustomerIdentifications = (customerNumberId: number): Observable<any> => this.getMaxResult('Customer/' + customerNumberId + '/Identifications/History');

    public getIdentificationLookups = (): Observable<CustomerIdentificationLookup> => this.getMaxResult('Customer/Identification/Lookups');

    public saveCustomerIdentification = (identification: any): Observable<any> => this.post('Customer/Identifications', identification, this.replacer);

    /* CERTIFICATIONS*/
    public getCustomerCertifications = (customerNumberId: number): Observable<any> => this.getMaxResult('Customer/' + customerNumberId + '/Certifications');

    public getCustomerCertification = (certificationId: number): Observable<any> => this.getMaxResult('Customer/Certifications/' + certificationId);

    public getHistoricalCustomerCertifications = (customerNumberId: number): Observable<any> => this.getMaxResult('Customer/' + customerNumberId + '/Certifications/History');

    public saveCustomerCertification = (certification: any): Observable<any> => this.post('Customer/Certifications', certification, this.replacer)
        .pipe(catchError((error) => this.handleError(error)));

    /* CHARACTERISTICS*/
    public getCustomerCharacteristics = (customerNumberId: number): Observable<any> => this.getMaxResult('Customer/' + customerNumberId + '/Characteristics');

    public getCustomerHistoricalCharacteristic = (customerNumberId: any): Observable<any> => this.getMaxResult('Customer/' + customerNumberId + '/Characteristics/History');

    public getCustomerCharacteristic = (characteristicId: number): Observable<any> => this.getMaxResult('Customer/Characteristics/' + characteristicId);

    public saveCustomerCharacteristic = (characteristic: any): Observable<any> => this.post('Customer/Characteristics', characteristic, this.replacer)
        .pipe(catchError((error) => this.handleError(error)));

    /* ATTRIBUTES*/
    public getCustomerAttributes = (customerNumberId: number): Observable<any> => this.getMaxResult('Customer/' + customerNumberId + '/Attributes');

    public getHistoricalCustomerAttributes = (customerNumberId: number): Observable<CustomerIndividualBiometric[]> => this.getMaxResult('Customer/' + customerNumberId + '/Attributes/History');

    public saveCustomerAttributes = (attributes: any): Observable<any> => this.post('Customer/Attributes', attributes, this.replacer)
        .pipe(catchError((error) => this.handleError(error)));

    /* AFFILIATIONS*/
    public getCustomerAffiliations = (customerNumberId: number): Observable<any> => this.getMaxResult('Customer/' + customerNumberId + '/Affiliations');

    public getCustomerAffiliation = (affiliationId: number): Observable<CustomerAffiliation> => this.getMaxResult('Customer/Affiliations/' + affiliationId);

    public getCustomerAffiliationRoleTypes = (affiliationTypeId: number): Observable<any> => this.getMaxResult('Customer/Affiliations/Roles/Types/' + affiliationTypeId );

    public getHistoricalCustomerAffiliations = (customerNumberId: number): Observable<any> => this.getMaxResult('Customer/' + customerNumberId + '/Affiliations/History');

    public saveCustomerAffiliation = (affiliation: any): Observable<any> => this.post('Customer/Affiliations', affiliation)
        .pipe(catchError((error) => this.handleError(error)));

    /* MEDICAL ALERTS*/
    public getCustomerMedicalAlerts = (customerNumberId: number): Observable<any> => this.getMaxResult('Customer/' + customerNumberId + '/MedicalAlerts');

    public getMedicalAlerts = (): Observable<CustomerIndividualMedicalAlertType[]> => this.getMaxResult('Customer/MedicalAlerts');

    public getCustomerMedicalConditions = (customerNumberId: number): Observable<CustomerIndividualMedicalCondition[]> => this.getMaxResult(`Customer/${customerNumberId}/MedicalCondition`);

    public getCustomerMedicalConditionById = (customerMedicalConditionId: number): Observable<any> => this.getMaxResult(`Customer/MedicalCondition/${customerMedicalConditionId}`);

    public getCustomerCategoryMedicalConditions = (customerNumberId: number, conditionCategoryTypeId: number): Observable<any> => this.getMaxResult('Customer/' + customerNumberId + '/MedicalCondition/Category/' + conditionCategoryTypeId);

    public getMedicalConditions = (): Observable<any> => this.getMaxResult('Customer/MedicalCondition');

    public getMedicalAlertLookups = (): Observable<CustomerIndividualMedicalLookup> => this.getMaxResult('Customer/MedicalAlert/Lookup');

    public getCustomerMedicalAlert = (medicalAlertId: number): Observable<any> => this.getMaxResult('Customer/MedicalAlerts/' + medicalAlertId);

    public getHistoricalCustomerMedicalAlerts = (customerNumberId: number): Observable<any> => this.getMaxResult('Customer/' + customerNumberId + '/MedicalAlerts/History');

    public saveCustomerMedicalAlert = (medicalAlert: any): Observable<any> => this.post('Customer/MedicalAlerts', medicalAlert)
        .pipe(catchError((error) => this.handleError(error)));

    public saveCustomerMedicalAlerts = (medicalAlerts: any): Observable<any> => this.post('Customer/MedicalAlerts/Bulk', medicalAlerts)
        .pipe(catchError((error) => this.handleError(error)));

    public saveCustomerMedicalConditions = (medicalConditions: any): Observable<any> => this.post('Customer/MedicalCondition', medicalConditions);

    public deleteCustomerMedicalCondition = (medicalConditionId: number): Observable<any> => this.delete(`Customer/MedicalCondition/${medicalConditionId}`)
        .pipe(catchError((error) => this.handleError(error)));

    /* Customer Representatives*/
    public getCustomerRepresentatives = (customerNumberId: number): Observable<CustomerRepresentative[]> => this.getMaxResult('Customer/Representative/customerNumberId/' + customerNumberId);

    public getCustomerRepresentative = (customerRepresentativeId: number): Observable<CustomerRepresentative> => this.getMaxResult('Customer/Representative/' + customerRepresentativeId);

    public getHistoricalCustomerRepresentatives = (customerNumberId: number): Observable<any[]> => this.getMaxResult('Customer/Representative/' + customerNumberId + '/History');

    public saveCustomerRepresentative = (representative: any): Observable<any> => this.post('customer/representative', representative, this.replacer)
        .pipe(catchError((error) => this.handleError(error)));

    public getLookups = (): Observable<any> => this.getMaxResult('customer/AuthorizedPresence/Lookups');

    public getAuthorizedPresence = (customerNumberId: number): Observable<any> => this.get(`customer/${customerNumberId}/AuthorizedPresence`);

    public getAuthorizedPresenceHistory = (customerNumberId: number): Observable<AuthorizedPresenceHistory[]> => this.getMaxResult(`customer/${customerNumberId}/AuthorizedPresence/History`);

    public saveAuthorizedPresence = (authorizedPresence: any): Observable<any> => this.post('customer/AuthorizedPresence', authorizedPresence);

    public unassignedEmailExists = (email: string): Observable<any> => this.getMaxResult('customer/mvdnow/user/exists?email=' + email);

    /* Add Customer*/
    public addIndividualCustomer = (customer: any): Observable<any> => this.post('Customer/add/individual', customer, this.replacer);

    public addOrganizationCustomer = (customer: any): Observable<any> => this.post('Customer/add/organization', customer, this.replacer);

    /* MVD now status */
    public getMvdNowStatus = (customerNumber: any): Observable<CustomerMvdNowStatus> => this.getMaxResult('Customer/' + customerNumber + '/MvdNow/Status');

    public setMvdNowAccess = (customerNumber: any, status: boolean): Observable<any> => this.post('Customer/' + customerNumber + '/MvdNow/Access', status )
        .pipe(catchError((error) => this.handleError(error)));

    public resetPhoneAuthenticationMethod = (customerNumber: any): Observable<any> => this.post('Customer/' + customerNumber + '/MvdNow/PhoneAuthentication/Reset', null)
        .pipe(catchError((error) => this.handleError(error)));

    public resetPhoneAuthenticationMethodByObjectId = (b2cObjectId: string): Observable<any> => this.post('Customer/MvdNow/ObjectId/' + b2cObjectId + '/PhoneAuthentication/Reset', null)
        .pipe(catchError((error) => this.handleError(error)));

    public unlinkFromMvdNow = (customerNumber: number): Observable<any> => this.post('Customer/' + customerNumber + '/MvdNow/Unlink', null);

    public deactivateEnhancedAuthentication = (customerNumber: number): Observable<any> => this.post('Customer/' + customerNumber + '/MvdNow/EnhancedAuthentication/Deactivate', null);

    public revokeEIdCredentials = (customerNumber: number): Observable<any> => this.post('EId/Registration/' + customerNumber + '/EnhancedIdentification/Revoke', null);

    public reinstateEIdCredentials = (customerNumber: number): Observable<any> => this.post('EId/Registration/' + customerNumber + '/EnhancedIdentification/Reinstate', null);

    public resetMVDNowAccountVerification = (customerNumber: number): Observable<any> => this.post(`Customer/${customerNumber}/MvdNow/AccountVerification/Reset`, null);

    public isCustomerRestricted = (customerNumberId: number): Observable<any> => this.get(`Customer/Characteristics/IsRestricted/Cached/${customerNumberId}`);

    public linkCustomerToMvdNowUser = (customerNumber: number, mvdNowUserId: number): Observable<any> => this.post('Customer/' + customerNumber + '/MvdNow/' + mvdNowUserId + '/Link', null);

    public searchUnlinkedUsers = (criteria: MvdNowUserSearchCriteria): Observable<any> => this.post('Customer/MvdNow/SearchUnlinked', criteria);

    public bustCache = (customerNumberId: number): Observable<any> => this.post(`Customer/Characteristics/ResetCache/${customerNumberId}`, null);

    public changeEmail = (customerNumberId: number, email: string): Observable<any> => this.post(`Customer/${customerNumberId}/MvdNow/EmailAddress/Update`, email);

    /* Mobile Identification */
    public getMobileIdentificationCustomerAuthorization = (customerNumber: number): Observable<any> => this.get(`MobileIdentification/${customerNumber}/authorization`);

    public getMobileIdentificationNumber = (customerNumber: number): Observable<any> => this.get(`MobileIdentification/${customerNumber}`);

    public getMobileIdentificationNumbers = (customerNumber: number): Observable<any> => this.get(`MobileIdentification/${customerNumber}/allTypes`);

    public getMobileIdentificationNumberHistory = (customerNumber: number): Observable<any> => this.get(`MobileIdentification/${customerNumber}/history`).pipe(map(response => response.result));

    public prequalifyMobileIdentification = (customerNumber: number): Observable<any> => this.post(`MobileIdentification/${customerNumber}/prequalify`, null);

    public prequalifyMobileIdentificationRequest = (mobileIdentificationNumberId: number, customerNumber: number): Observable<any> => this.post(`MobileIdentification/${customerNumber}/prequalify/${mobileIdentificationNumberId}`, null);

    public forcedExtendedReviewMobileIdentification = (customerNumber: number): Observable<any> => this.post(`MobileIdentification/${customerNumber}/extendedReview`, null);

    /* Get is customer mobile id opted out */
    public isCustomerMobileIdOptedOut = (customerNumber: any): Observable<boolean> => this.getMaxResult('MobileIdentification/' + customerNumber + '/optout');

    public getMobileIdentificationCustomerAuthorizationHistory = (customerNumber: number): Observable<any> => this.get(`MobileIdentification/${customerNumber}/customerauthorization/history`).pipe(map(response => response.result));

    public restrictMobileIdentification = (customerNumber: number, reasonTypeId: number): Observable<any> => this.post(`MobileIdentification/${customerNumber}/restricted/reasonType/${reasonTypeId}`, null);

    public unrestrictMobileIdentification = (customerNumber: number): Observable<any> => this.post(`MobileIdentification/${customerNumber}/unrestricted`, null);

    public denyMobileIdentification = (customerNumber: number, mobileIdentificationCredentialTypeId: number): Observable<any> => this.post(`MobileIdentification/${customerNumber}/Type/${mobileIdentificationCredentialTypeId}/deny`, null);

    public pauseMobileIdentification = (mobileIdentificationCredentialActionRequest: MobileIdentificationCredentialActionRequest): Observable<any> => this.post('MobileIdentification/pause', mobileIdentificationCredentialActionRequest);

    public unlinkMobileProvisioningBundle = (provisioningBundleId: number, reasonTypeId: number): Observable<any> => this.post(`MobileIdentification/ProvisioningBundle/${provisioningBundleId}/unlink/reasonType/${reasonTypeId}`, null);

    public resumeMobileIdentification = (mobileIdentificationCredentialActionRequest: MobileIdentificationCredentialActionRequest): Observable<any> => this.post('MobileIdentification/resume', mobileIdentificationCredentialActionRequest);

    public getMobileIdUIEnabled = (): Observable<any> => this.getMaxResult('MobileIdentification/MobileIdUIEnabled');

    /* Idemia mID */
    public getLatestMId = (customerNumber: number): Observable<any> => this.get('MId/Registration/' + customerNumber + '/Latest');

    public revokeMIdCredentials = (customerNumber: number): Observable<any> => this.post('MId/Registration/' + customerNumber + '/EnhancedIdentification/Revoke', null);

    public reinstateMIdCredentials = (customerNumber: number): Observable<any> => this.post('MId/Registration/' + customerNumber + '/EnhancedIdentification/Reinstate', null);

    public updateMIdCredentials = (customerNumber: number): Observable<any> => this.post('MId/Registration/' + customerNumber + '/EnhancedIdentification/Update', null);

    private handleError(error: HttpErrorResponse) {
        if (error.error) {
            return observableThrowError(error.error);
        } else {
            return observableThrowError('Server error');
        }
    }

    private replacer(key, value) {
        if (value === '') {
            return undefined;
        }

        return value;
    }
}
